import { I18n } from '@calendly/i18n';
import { Avatar } from '@calendly/ui/components/avatar';
import { BareButton } from '@calendly/ui/components/bare-button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@calendly/ui/components/dropdown-menu';
import { ArrowChevronDownMiniIcon, ArrowChevronUpMiniIcon } from '@calendly/ui/components/icons';
import { BodyText, LabelText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import { colorTextSecondary, colorTextStandard } from '@calendly/ui/theme';
import classNames from 'classnames';
import { useCallback, useState, type FC, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { navigationAnalyticsIdForProfileDropdown, trackProfileDropdownNavigation, useTrack } from 'hooks/analytics_events';
import { PROFILE_MENU_POPOVER_OFFSET_PX, profileDropdownMenuSurfaceCss } from './profileMenuDropdownChrome';
import type { MainNavAvatarRowProfile } from './types';
const rootCss = "r1q7pxpg";
const triggerCss = "t1248sr4";
const textBlockCss = "tkwn7wh";
const avatarCellCss = "arpydks";
const chevronSlotCss = "c1119vnd";

/**
 * **`compactExpandedAvatarRow`**: root wrapper and trigger share one fixed width so the row fits beside the close
 * control (mobile **`fullExpandAndCollapse`** rail).
 */
const compactSharedWidthCss = "ca4zb1e";
const textBlockCompactCss = "t1uc114u";
const singleLineEllipsisCss = "sagsfqa";
const textAvatarNameCss = "t1rjmmfi";
const textAvatarBusinessNameCss = "t1kiwnz1";

/** Same shape as `CollapsedAvatarRowProps`: nested `profile` and/or spread fields from `MainNavAvatarRowProfile`. */
export type ExpandedAvatarRowProps = Partial<MainNavAvatarRowProfile> & {
  profile?: Partial<MainNavAvatarRowProfile>;
  /** Profile dropdown body; composed by the parent so this row stays free of billing/scheduling/Redux menu graph. */
  profileMenu: ReactNode;
  /**
   * Mobile drawer header with a trailing close control: shrink root / trigger / text column per shell layout tokens
   * so the avatar row and **X** share the fixed rail width.
   */
  compactExpandedAvatarRow?: boolean;
};

/**
 * Expanded rail header: avatar, display name, organization line, and menu chevron. Opens `profileMenu` in a dropdown
 * anchored to this row (CUI `VerticalNav` expanded state).
 */
export const ExpandedAvatarRow: FC<ExpandedAvatarRowProps> = props => {
  const {
    profile,
    profileMenu,
    compactExpandedAvatarRow = false,
    ...overrides
  } = props;
  const isPhone = useDeviceType('isPhone');
  const {
    fullName = '',
    businessName = '',
    avatarSrc,
    avatarLetter,
    menuAccessLabel
  } = {
    ...profile,
    ...overrides
  };

  /** Profile menu open state; drives dropdown visibility and chevron direction. */
  const [menuOpen, setMenuOpen] = useState(false);
  const track = useTrack();
  const {
    pathname
  } = useLocation();
  const toggleProfileMenu = useCallback(() => {
    setMenuOpen(open => {
      if (!open) {
        trackProfileDropdownNavigation(track, 'profile_dropdown', pathname);
      }
      return !open;
    });
  }, [pathname, track]);

  /** Single visible character in the avatar fallback when there is no photo. */
  const resolvedLetter = avatarLetter ?? (fullName.trim().charAt(0) || undefined);

  /** Announces the control to assistive tech; prefers an explicit menu label when the product provides one. */
  const ariaLabel = menuAccessLabel ?? I18n.t('publisher.app_shell.avatar_row.account_menu_aria_label', {
    full_name: fullName,
    business_name: businessName
  });
  return <div className={classNames(rootCss, compactExpandedAvatarRow && compactSharedWidthCss)}>
      <DropdownMenu isOpen={menuOpen} onClose={() => setMenuOpen(false)}>
        <DropdownMenuTrigger>
          <BareButton type="button" className={classNames(triggerCss, compactExpandedAvatarRow && compactSharedWidthCss)} onClick={toggleProfileMenu} aria-haspopup="menu" aria-expanded={menuOpen} aria-label={ariaLabel} data-analytics-id={navigationAnalyticsIdForProfileDropdown('profile_dropdown')}>
            <span className={avatarCellCss} aria-hidden>
              <Avatar size="small" src={avatarSrc} letter={resolvedLetter} />
            </span>
            <div className={classNames(textBlockCss, compactExpandedAvatarRow && textBlockCompactCss)}>
              <div className={classNames(singleLineEllipsisCss, textAvatarNameCss)}>
                <LabelText size="large" color={colorTextStandard}>
                  {fullName}
                </LabelText>
              </div>
              {businessName && <div className={classNames(singleLineEllipsisCss, textAvatarBusinessNameCss)}>
                  <BodyText size="small" color={colorTextSecondary} title={businessName} inline>
                    {businessName}
                  </BodyText>
                </div>}
            </div>
            <span className={chevronSlotCss}>
              {menuOpen ? <ArrowChevronUpMiniIcon title="" /> : <ArrowChevronDownMiniIcon title="" />}
            </span>
          </BareButton>
        </DropdownMenuTrigger>
        {/*
         * Phone: omit fixed surface width, `placement`, and `offset` so CUI’s mobile bottom-sheet menu controls layout.
         * Desktop: keep narrow popover surface + anchored placement under the rail trigger.
         */}
        <DropdownMenuContent className={isPhone ? undefined : profileDropdownMenuSurfaceCss} {...isPhone ? {} : {
        placement: 'bottom start' as const,
        offset: PROFILE_MENU_POPOVER_OFFSET_PX
      }}>
          {profileMenu}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>;
};

require("./ExpandedAvatarRow.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./ExpandedAvatarRow.tsx");